import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatMoney, formatMetric, currencySymbol } from '@/utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAccount, setEditingAccount] = useState(null);
  const [editForm, setEditForm] = useState({ leverage: 100, account_type: 'STANDARD', is_active: true });
  const [query, setQuery] = useState('');

  // Real-time client-side filter across name / email / account id / currency.
  // Client-side (not backend) so typing feels instant even at large N and no
  // extra API load — the full accounts list is already loaded once.
  const filteredAccounts = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(a =>
      (a.client_name || '').toLowerCase().includes(q) ||
      (a.client_email || '').toLowerCase().includes(q) ||
      String(a.id || '').toLowerCase().includes(q) ||
      String(a.account_number || '').toLowerCase().includes(q) ||
      (a.currency || 'USD').toLowerCase().includes(q)
    );
  }, [accounts, query]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/admin/accounts`);
      setAccounts(response.data);
    } catch (error) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAccounts(); }, []);

  const openEdit = (account) => {
    setEditingAccount(account);
    setEditForm({ leverage: account.leverage, account_type: account.account_type, is_active: account.is_active });
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/admin/accounts/${editingAccount.id}`, { ...editForm, reason: 'Admin update' });
      toast.success('Account updated');
      setEditingAccount(null);
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to update account');
    }
  };

  const adjustBalance = async (accountId, ccy = 'USD') => {
    const amount = prompt(`Enter adjustment amount in ${ccy} (negative to deduct):`);
    if (!amount) return;
    const reason = prompt('Enter reason for adjustment:');
    if (!reason) return;
    try {
      await axios.post(`${API}/admin/accounts/${accountId}/adjust-balance`, { amount: parseFloat(amount), reason });
      toast.success('Balance adjusted');
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust balance');
    }
  };

  return (
    <div className="p-8" data-testid="admin-accounts-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-brand-primary">Trading Accounts</h1>
          <p className="text-slate-600 mt-1" data-testid="admin-accounts-count">
            {query ? `${filteredAccounts.length} of ${accounts.length}` : `${accounts.length}`} accounts
          </p>
        </div>
        <div className="relative w-full sm:w-96">
          <Input
            data-testid="admin-accounts-search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search name, email, account ID, currency…"
            className="pr-9"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              data-testid="admin-accounts-search-clear"
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-slate-500 hover:text-slate-800 hover:bg-slate-100"
            >
              ×
            </button>
          )}
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left uppercase text-xs tracking-wider text-slate-600">
              <th className="p-4">Client</th>
              <th className="p-4">Type</th>
              <th className="p-4">Ccy</th>
              <th className="p-4 text-right">Balance</th>
              <th className="p-4 text-right">Equity</th>
              <th className="p-4 text-right">Margin</th>
              <th className="p-4 text-right">Free Margin</th>
              <th className="p-4">Leverage</th>
              <th className="p-4">Status</th>
              <th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="10" className="p-8 text-center"><div className="loader mx-auto"></div></td></tr>
            ) : filteredAccounts.length === 0 ? (
              <tr><td colSpan="10" className="p-8 text-center text-slate-500" data-testid="admin-accounts-empty">No trading accounts found.</td></tr>
            ) : filteredAccounts.map(account => (
              <tr key={account.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-4">
                  <div className="font-medium">{account.client_name}</div>
                  <div className="text-xs text-slate-500">{account.client_email}</div>
                </td>
                <td className="p-4">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">{account.account_type}</span>
                </td>
                <td className="p-4" data-testid={`admin-account-ccy-${account.id}`}>
                  <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded text-xs font-mono">{account.currency || 'USD'}</span>
                </td>
                <td className="p-4 text-right tabular-nums font-semibold">{formatMoney(account.balance, account.currency)}</td>
                <td className="p-4 text-right tabular-nums" data-testid={`admin-account-equity-${account.id}`}>{formatMetric(account.equity, account.currency)}</td>
                <td className="p-4 text-right tabular-nums" data-testid={`admin-account-margin-${account.id}`}>{formatMetric(account.margin, account.currency)}</td>
                <td className="p-4 text-right tabular-nums" data-testid={`admin-account-free-margin-${account.id}`}>{formatMetric(account.free_margin, account.currency)}</td>
                <td className="p-4">1:{account.leverage}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${account.is_active ? 'bg-trading-bullish-bg text-trading-bullish' : 'bg-trading-bearish-bg text-trading-bearish'}`}>
                    {account.is_active ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(account)} data-testid={`admin-edit-account-${account.id}`}>Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => adjustBalance(account.id, account.currency)} data-testid={`admin-adjust-balance-${account.id}`}>Adjust {currencySymbol(account.currency)}</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editingAccount} onOpenChange={(open) => !open && setEditingAccount(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Account</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Account Type</Label>
              <Select value={editForm.account_type} onValueChange={v => setEditForm({...editForm, account_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="STANDARD">Standard</SelectItem>
                  <SelectItem value="PROFESSIONAL">Professional</SelectItem>
                  <SelectItem value="VIP">VIP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Leverage</Label>
              <Select value={String(editForm.leverage)} onValueChange={v => setEditForm({...editForm, leverage: parseInt(v)})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">1:30</SelectItem>
                  <SelectItem value="50">1:50</SelectItem>
                  <SelectItem value="100">1:100</SelectItem>
                  <SelectItem value="200">1:200</SelectItem>
                  <SelectItem value="500">1:500</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={String(editForm.is_active)} onValueChange={v => setEditForm({...editForm, is_active: v === 'true'})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} data-testid="admin-save-account">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAccounts;
